/**
 * Phase 1b · Slice 2b — Refunds & Adjustments admin panel.
 *
 * Every validation, sign-flip, and cap enforcement is done by the
 * backend. This panel is a UX shell:
 *   1. search / select an order,
 *   2. render frozen original amounts,
 *   3. render prior adjustments audit trail,
 *   4. choose adjustment type + amounts,
 *   5. preview resulting totals,
 *   6. confirm dangerous actions,
 *   7. submit once (idempotency key regenerated per open form),
 *   8. render success + audit reference,
 *   9. list per-adjustment "Reverse" button (with confirmation).
 */
import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { AlertTriangle, RefreshCw } from "lucide-react";
import {
  ADJUSTMENT_TYPES, DANGEROUS_ADJUSTMENT_TYPES,
  fieldsForType, validateForm, previewResultingTotals,
  humanizeError, formatAdjCents as fmt,
} from "./adminAdjustmentsHelpers";

function newIdempotencyKey() {
  return (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `k-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

import { ExportRefundsCsvButton } from "@/components/admin/RefundHistoryCard";

export function RefundsAdjustmentsPanel() {
  const [sourceType, setSourceType] = useState("sticker_order");
  const [orderId, setOrderId] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewErr, setPreviewErr] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadPreview = async () => {
    if (!orderId.trim()) return;
    setLoading(true); setPreviewErr(null); setPreview(null);
    try {
      const r = await api.get(
        `/admin/sales/adjustments/order-preview?source_type=${sourceType}&source_order_id=${encodeURIComponent(orderId.trim())}`,
      );
      setPreview(r.data);
    } catch (e) {
      setPreviewErr(humanizeError(formatApiError(e?.response?.data?.detail)));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="rounded-2xl border-2 border-[var(--ink)] bg-white p-4 space-y-3"
             data-testid="refunds-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-display text-lg">Refunds &amp; Adjustments</h2>
        <div className="flex items-center gap-2 flex-wrap">
          <ExportRefundsCsvButton preset="this_year" />
          <div className="text-[10px] uppercase text-[var(--smoke)] tracking-widest">
            Append-only · admin-only · no destructive edits · reversal supported
          </div>
        </div>
      </div>

      {/* Order selector */}
      <div className="flex flex-wrap gap-2 items-end">
        <label className="text-xs">
          <div className="uppercase text-[10px] text-[var(--smoke)]">Source</div>
          <select value={sourceType} onChange={e => setSourceType(e.target.value)}
                  data-testid="adj-source-type"
                  className="border-2 border-[var(--ink)] rounded-full px-3 py-1 text-sm">
            <option value="sticker_order">Sticker order</option>
            <option value="certificate_order">Certificate order</option>
          </select>
        </label>
        <label className="text-xs flex-1 min-w-[200px]">
          <div className="uppercase text-[10px] text-[var(--smoke)]">Internal order id</div>
          <input value={orderId} onChange={e => setOrderId(e.target.value)}
                 data-testid="adj-order-id"
                 placeholder="paste order.id (uuid) from admin list"
                 className="w-full border-2 border-[var(--ink)] rounded-full px-3 py-1 text-sm" />
        </label>
        <button onClick={loadPreview} disabled={!orderId || loading}
                data-testid="adj-load-preview"
                className="border-2 border-[var(--ink)] rounded-full px-3 py-1 text-xs uppercase font-bold bg-[var(--ink)] text-white disabled:opacity-40">
          {loading ? "Loading…" : "Load order"}
        </button>
      </div>

      {previewErr && (
        <div className="p-3 border-2 border-red-500 bg-red-50 rounded-lg text-red-800 text-sm"
             data-testid="adj-preview-error">{previewErr}</div>
      )}

      {preview && <PreviewCard preview={preview} onReload={loadPreview} />}
    </section>
  );
}

function PreviewCard({ preview, onReload }) {
  return (
    <div className="rounded-xl border-2 border-neutral-200 p-3 space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="font-mono text-sm">
          Order · <b>{preview.order.public_order_reference}</b> ·
          {" "}{preview.order.source_type} · reporting bucket:{" "}
          <span className="uppercase">{preview.order.reporting_bucket}</span>
        </div>
        <button onClick={onReload}
                data-testid="adj-refresh-preview"
                className="text-xs uppercase font-bold inline-flex items-center gap-1">
          <RefreshCw size={12} /> refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
        <MetricPair label="Original gross" value={preview.original.gross_sales_cents} />
        <MetricPair label="Original net merch" value={preview.original.net_merchandise_sales_cents} />
        <MetricPair label="Original tax" value={preview.original.tax_collected_cents} />
        <MetricPair label="Original shipping" value={preview.original.shipping_charged_cents} />
        <MetricPair label="Original customer total" value={preview.original.customer_total_cents} />
        <MetricPair label="Original amount paid" value={preview.original.amount_paid_cents} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs bg-neutral-50 border-2 border-dashed border-neutral-300 rounded-lg p-2"
           data-testid="adj-remaining">
        <MetricPair label="Merch remaining" value={preview.remaining.merchandise_remaining} highlight />
        <MetricPair label="Tax remaining" value={preview.remaining.tax_remaining} highlight />
        <MetricPair label="Shipping remaining" value={preview.remaining.shipping_remaining} highlight />
        <MetricPair label="Customer total remaining" value={preview.remaining.customer_total_remaining_refundable} highlight />
      </div>

      {preview.prior_adjustments.length > 0 && (
        <PriorAdjustmentsList rows={preview.prior_adjustments} onReversed={onReload} />
      )}

      <NewAdjustmentForm preview={preview} onSubmitted={onReload} />
    </div>
  );
}

function MetricPair({ label, value, highlight }) {
  return (
    <div className={`p-2 rounded ${highlight ? "bg-white border-2 border-[var(--ink)]" : ""}`}>
      <div className="uppercase text-[10px] text-[var(--smoke)]">{label}</div>
      <div className="font-mono">{fmt(value)}</div>
    </div>
  );
}

function PriorAdjustmentsList({ rows, onReversed }) {
  return (
    <div className="space-y-1" data-testid="adj-prior-list">
      <div className="font-bold text-xs uppercase tracking-widest">Prior adjustments · audit trail</div>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-[var(--smoke)]">
            <th>When</th><th>Type</th><th>Amount</th><th>Actor</th><th>Reason</th><th></th>
          </tr>
        </thead>
        <tbody>
          {rows.map(a => (
            <tr key={a.adjustment_id} className="border-t border-neutral-100"
                data-testid={`adj-row-${a.adjustment_id}`}>
              <td className="py-1 font-mono">{a.effective_at?.slice(0, 19)}</td>
              <td className="py-1">{a.adjustment_type}</td>
              <td className="py-1 font-mono">{fmt(a.total_adjustment_cents)}</td>
              <td className="py-1 text-[10px]">{a.admin_display_name}</td>
              <td className="py-1 italic">{a.reason || "—"}</td>
              <td className="py-1 text-right">
                {a.adjustment_type !== "adjustment_reversal" && (
                  <ReverseButton adjustmentId={a.adjustment_id} onDone={onReversed} />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ReverseButton({ adjustmentId, onDone }) {
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState("");
  const [err, setErr] = useState(null);
  const submit = async () => {
    setErr(null);
    try {
      await api.post(`/admin/sales/adjustments/${adjustmentId}/reverse`, {
        idempotency_key: newIdempotencyKey(),
        reason: reason || "Reversal",
      });
      setConfirming(false); setReason("");
      onDone && onDone();
    } catch (e) {
      setErr(humanizeError(formatApiError(e?.response?.data?.detail)));
    }
  };
  if (!confirming) {
    return (
      <button onClick={() => setConfirming(true)}
              data-testid={`adj-reverse-${adjustmentId}`}
              className="text-[10px] uppercase font-bold text-red-700 border-2 border-red-700 rounded-full px-2 py-0.5">
        Reverse
      </button>
    );
  }
  return (
    <div className="text-[10px]">
      <input value={reason} onChange={e => setReason(e.target.value)}
             placeholder="reason (optional)"
             className="border rounded px-1 py-0.5" />
      <button onClick={submit}
              data-testid={`adj-reverse-confirm-${adjustmentId}`}
              className="ml-1 uppercase font-bold text-red-700">Confirm</button>
      <button onClick={() => setConfirming(false)}
              className="ml-1 uppercase">Cancel</button>
      {err && <div className="text-red-700">{err}</div>}
    </div>
  );
}

function NewAdjustmentForm({ preview, onSubmitted }) {
  const [type, setType] = useState("");
  const [values, setValues] = useState({});
  const [errs, setErrs] = useState([]);
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [serverErr, setServerErr] = useState(null);
  const [key, setKey] = useState(newIdempotencyKey);

  useEffect(() => { setValues({}); setConfirming(false); setResult(null); setServerErr(null); }, [type]);
  useEffect(() => { setKey(newIdempotencyKey()); }, [preview]);

  const fields = fieldsForType(type);
  const previewTotals = useMemo(() => previewResultingTotals(type, values, preview),
                                 [type, values, preview]);

  const validate = () => {
    const e = validateForm(type, values, preview);
    setErrs(e); return e.length === 0;
  };

  const submit = async () => {
    if (!validate()) return;
    setSubmitting(true); setServerErr(null);
    try {
      const num = (k) => Math.max(0, Math.round(Number(values[k] || 0) * 100));
      const r = await api.post(`/admin/sales/adjustments`, {
        source_type: preview.order.source_type,
        source_order_id: preview.order.public_order_reference, // fallback — real id from admin list
        source_order_id_hint: null,
        adjustment_type: type,
        merchandise_adjustment_cents: num("merchandise_cents"),
        tax_adjustment_cents: num("tax_cents"),
        shipping_adjustment_cents: num("shipping_cents"),
        discount_adjustment_cents: num("discount_cents"),
        refund_quantity: Number(values.refund_quantity || 0),
        line_item_reference: values.line_item_reference || null,
        reason: values.reason || null,
        idempotency_key: key,
      });
      setResult(r.data);
      setConfirming(false);
      onSubmitted && onSubmitted();
    } catch (e) {
      setServerErr(humanizeError(formatApiError(e?.response?.data?.detail)));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="border-t-2 border-[var(--ink)] pt-3 space-y-2" data-testid="adj-new-form">
      <div className="font-bold text-xs uppercase tracking-widest">New adjustment</div>
      <div className="flex flex-wrap gap-2 items-end">
        <label className="text-xs">
          <div className="uppercase text-[10px] text-[var(--smoke)]">Type</div>
          <select value={type} onChange={e => setType(e.target.value)}
                  data-testid="adj-type"
                  className="border-2 border-[var(--ink)] rounded-full px-3 py-1 text-sm">
            <option value="">— choose —</option>
            {ADJUSTMENT_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        {fields.includes("merchandise") && (
          <NumField label="Merch $" testId="adj-merch" values={values} setValues={setValues} field="merchandise_cents" />
        )}
        {fields.includes("tax") && (
          <NumField label="Tax $" testId="adj-tax" values={values} setValues={setValues} field="tax_cents" />
        )}
        {fields.includes("shipping") && (
          <NumField label="Ship $" testId="adj-ship" values={values} setValues={setValues} field="shipping_cents" />
        )}
        {fields.includes("discount") && (
          <NumField label="Discount $" testId="adj-disc" values={values} setValues={setValues} field="discount_cents" />
        )}
        {fields.includes("line_item") && (
          <label className="text-xs">
            <div className="uppercase text-[10px] text-[var(--smoke)]">Line item</div>
            <select value={values.line_item_reference || ""}
                    onChange={e => setValues(v => ({ ...v, line_item_reference: e.target.value }))}
                    data-testid="adj-line-item"
                    className="border-2 border-[var(--ink)] rounded-full px-3 py-1 text-sm">
              <option value="">— choose —</option>
              {preview.line_items.map(li => (
                <option key={li.line_item_reference} value={li.line_item_reference}>
                  {li.product_name || li.line_item_reference} · remain {li.remaining_quantity}
                </option>
              ))}
            </select>
          </label>
        )}
        {fields.includes("quantity") && (
          <NumField label="Qty" testId="adj-qty" values={values} setValues={setValues}
                    field="refund_quantity" step={1} />
        )}
        {(fields.includes("reason") || REASON_REQUIRED_TYPES_HELPER(type)) && (
          <label className="text-xs flex-1 min-w-[180px]">
            <div className="uppercase text-[10px] text-[var(--smoke)]">Reason</div>
            <input value={values.reason || ""} onChange={e => setValues(v => ({ ...v, reason: e.target.value }))}
                   data-testid="adj-reason"
                   className="w-full border-2 border-[var(--ink)] rounded-full px-3 py-1 text-sm" />
          </label>
        )}
      </div>

      {previewTotals && (
        <div className="text-xs bg-yellow-50 border-2 border-yellow-500 rounded-lg p-2"
             data-testid="adj-preview-totals">
          Preview: <b>{fmt(previewTotals.total_cents)}</b> refund →
          resulting customer total = <b>{fmt(previewTotals.resulting_customer_total_cents)}</b>
          {previewTotals.exceeds_remaining && (
            <span className="ml-2 text-red-700">exceeds remaining refundable — will be rejected</span>
          )}
        </div>
      )}
      {errs.length > 0 && (
        <ul className="text-xs text-red-700 list-disc list-inside" data-testid="adj-client-errors">
          {errs.map(e => <li key={e}>{e}</li>)}
        </ul>
      )}
      {serverErr && (
        <div className="text-xs text-red-700 border-2 border-red-500 bg-red-50 p-2 rounded"
             data-testid="adj-server-error">{serverErr}</div>
      )}
      {result && (
        <div className="text-xs text-emerald-800 border-2 border-emerald-500 bg-emerald-50 p-2 rounded"
             data-testid="adj-success">
          Recorded · adjustment_id = <span className="font-mono">{result.adjustment_id}</span>
          · idempotent_hit = {String(result.idempotent_hit)}
        </div>
      )}
      <div className="flex gap-2">
        {!confirming && !result && (
          <button onClick={() => (validate() && (DANGEROUS_ADJUSTMENT_TYPES.has(type) ? setConfirming(true) : submit()))}
                  disabled={submitting || !type}
                  data-testid="adj-submit"
                  className="border-2 border-[var(--ink)] bg-[var(--ink)] text-white rounded-full px-4 py-1 text-xs font-bold uppercase disabled:opacity-40">
            {submitting ? "Submitting…" : (DANGEROUS_ADJUSTMENT_TYPES.has(type) ? "Review" : "Submit")}
          </button>
        )}
        {confirming && (
          <>
            <div className="text-xs flex items-center gap-1 text-red-800">
              <AlertTriangle size={12} /> Confirm this dangerous adjustment
            </div>
            <button onClick={submit}
                    data-testid="adj-confirm-submit"
                    className="border-2 border-red-700 bg-red-700 text-white rounded-full px-4 py-1 text-xs font-bold uppercase">
              Confirm &amp; submit once
            </button>
            <button onClick={() => setConfirming(false)}
                    className="uppercase text-xs">Cancel</button>
          </>
        )}
        {result && (
          <button onClick={() => { setResult(null); setType(""); setValues({}); }}
                  data-testid="adj-new-again"
                  className="text-xs uppercase font-bold border-2 border-[var(--ink)] rounded-full px-3 py-1">
            New adjustment
          </button>
        )}
      </div>
    </div>
  );
}

function NumField({ label, testId, values, setValues, field, step = 0.01 }) {
  return (
    <label className="text-xs">
      <div className="uppercase text-[10px] text-[var(--smoke)]">{label}</div>
      <input type="number" min={0} step={step} value={values[field] ?? ""}
             onChange={e => setValues(v => ({ ...v, [field]: e.target.value }))}
             data-testid={testId}
             className="w-24 border-2 border-[var(--ink)] rounded-full px-3 py-1 text-sm" />
    </label>
  );
}

// Local helper so we don't import a private name for the "reason" field.
function REASON_REQUIRED_TYPES_HELPER(t) {
  return t === "manual_financial_correction";
}
